/*
  Marqforde change (25 Sept 2026): mobile lightbox.
  - Phone back button (and iOS back swipe) closes the lightbox instead of leaving the page.
  - Under 750px: swipeable carousel of every product image with an "n / total" counter.
  - Zoom handled here rather than by the browser: pinch or double-tap to zoom, one finger
    to pan while zoomed, pinch out or double-tap again to reset. The carousel is frozen
    while zoomed.
  - Swipe down, or tap outside the picture, to close (not while zoomed).
  Desktop keeps the stock behaviour apart from the back button.
*/
if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mql = window.matchMedia('(max-width: 749px)');
        this.content = this.querySelector('[role="document"]');
        this.counter = this.querySelector('.product-media-modal__counter');
        this.onPopState = this.onPopState.bind(this);
        this.historyPushed = false;
        this.zoom = { img: null, s: 1, x: 0, y: 0 };
        this.gesture = null;
        this.lastTap = 0;

        if (this.content) {
          this.content.addEventListener('scroll', () => this.updateCounter(), { passive: true });
          this.content.addEventListener('click', this.onContentClick.bind(this));
          this.content.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
          this.content.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
          this.content.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false });
          this.content.addEventListener('touchcancel', this.onTouchEnd.bind(this), { passive: false });
        }
      }

      hide(fromHistory) {
        window.removeEventListener('popstate', this.onPopState);
        this.resetZoom();
        super.hide();
        this.classList.remove('lb-carousel');
        if (this.counter) this.counter.hidden = true;
        if (this.historyPushed) {
          this.historyPushed = false;
          if (fromHistory !== true && history.state && history.state.productModal) history.back();
        }
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        try {
          if (!history.state || !history.state.productModal) {
            history.pushState(Object.assign({}, history.state, { productModal: true }), '');
            this.historyPushed = true;
          }
          window.addEventListener('popstate', this.onPopState);
        } catch (e) {
          this.historyPushed = false;
        }
      }

      onPopState() {
        if (this.hasAttribute('open')) this.hide(true);
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        if (this.mql.matches && this.content) {
          this.buildCarousel(activeMedia);
        } else {
          this.classList.remove('lb-carousel');
          activeMedia.scrollIntoView();
          const container = this.querySelector('[role="document"]');
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      buildCarousel(activeMedia) {
        this.resetZoom();
        this.slides = Array.from(this.content.children).filter((el) => el.dataset && el.dataset.mediaId);
        this.slides.forEach((el) => el.classList.add('lb-in'));
        this.classList.add('lb-carousel');
        requestAnimationFrame(() => {
          this.content.scrollLeft = activeMedia.offsetLeft;
          this.updateCounter();
        });
      }

      updateCounter() {
        if (!this.counter || !this.classList.contains('lb-carousel') || !this.slides) return;
        const total = this.slides.length;
        if (total < 2) {
          this.counter.hidden = true;
          return;
        }
        const width = this.content.clientWidth || 1;
        const index = Math.min(total, Math.max(1, Math.round(this.content.scrollLeft / width) + 1));
        this.counter.textContent = `${index} / ${total}`;
        this.counter.hidden = false;
      }

      /* ---------- zoom ---------- */

      isZoomed() {
        return this.zoom.s > 1.01;
      }

      zoomBounds(img, s) {
        const cw = img.clientWidth;
        const ch = img.clientHeight;
        const nw = img.naturalWidth || cw;
        const nh = img.naturalHeight || ch;
        const fit = Math.min(cw / nw, ch / nh);
        return {
          x: Math.max(0, (nw * fit * s - cw) / 2),
          y: Math.max(0, (nh * fit * s - ch) / 2),
        };
      }

      applyZoom(img, s, x, y, animate) {
        const b = this.zoomBounds(img, s);
        x = Math.max(-b.x, Math.min(b.x, x));
        y = Math.max(-b.y, Math.min(b.y, y));
        this.zoom = { img, s, x, y };
        img.classList.toggle('lb-animate', !!animate);
        img.style.transform = s > 1.01 ? `translate(${x}px, ${y}px) scale(${s})` : '';
        this.classList.toggle('lb-zoomed', s > 1.01);
        if (s <= 1.01) this.zoom = { img: null, s: 1, x: 0, y: 0 };
      }

      resetZoom() {
        if (this.zoom.img) this.zoom.img.style.transform = '';
        this.zoom = { img: null, s: 1, x: 0, y: 0 };
        this.gesture = null;
        this.classList.remove('lb-zoomed');
      }

      centre() {
        const r = this.content.getBoundingClientRect();
        return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      }

      currentImg(target) {
        const img = target && target.closest ? target.closest('img.lb-in') : null;
        return img && img.naturalWidth ? img : null;
      }

      onTouchStart(event) {
        if (!this.mql.matches || !this.classList.contains('lb-carousel')) return;
        const t = event.touches;
        if (t.length === 2) {
          const img = this.zoom.img || this.currentImg(event.target);
          if (!img) return;
          event.preventDefault();
          const c = this.centre();
          this.gesture = {
            type: 'pinch',
            img,
            d0: Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY) || 1,
            s0: this.zoom.img === img ? this.zoom.s : 1,
            x0: this.zoom.img === img ? this.zoom.x : 0,
            y0: this.zoom.img === img ? this.zoom.y : 0,
            mx: (t[0].clientX + t[1].clientX) / 2 - c.x,
            my: (t[0].clientY + t[1].clientY) / 2 - c.y,
          };
          this.classList.add('lb-zoomed');
        } else if (t.length === 1) {
          if (this.isZoomed()) {
            this.gesture = { type: 'pan', sx: t[0].clientX, sy: t[0].clientY, x0: this.zoom.x, y0: this.zoom.y };
          } else {
            this.gesture = { type: 'swipe', sx: t[0].clientX, sy: t[0].clientY, time: Date.now() };
          }
        }
      }

      onTouchMove(event) {
        const g = this.gesture;
        if (!g) return;
        const t = event.touches;
        if (g.type === 'pinch' && t.length === 2) {
          event.preventDefault();
          const d = Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
          const s = Math.max(1, Math.min(4, (g.s0 * d) / g.d0));
          const x = g.mx - (g.mx - g.x0) * (s / g.s0);
          const y = g.my - (g.my - g.y0) * (s / g.s0);
          this.applyZoom(g.img, s, x, y, false);
        } else if (g.type === 'pan' && t.length === 1 && this.zoom.img) {
          event.preventDefault();
          this.applyZoom(
            this.zoom.img,
            this.zoom.s,
            g.x0 + (t[0].clientX - g.sx),
            g.y0 + (t[0].clientY - g.sy),
            false
          );
        }
      }

      onTouchEnd(event) {
        const g = this.gesture;
        const t = event.touches;
        if (g && g.type === 'pinch') {
          if (!this.isZoomed()) this.resetZoom();
          if (t.length === 1 && this.isZoomed()) {
            this.gesture = { type: 'pan', sx: t[0].clientX, sy: t[0].clientY, x0: this.zoom.x, y0: this.zoom.y };
          } else {
            this.gesture = null;
          }
          return;
        }
        if (g && g.type === 'swipe' && t.length === 0 && this.hasAttribute('open')) {
          const e = event.changedTouches[0];
          const dx = e.clientX - g.sx;
          const dy = e.clientY - g.sy;
          const onMedia = event.target.closest && event.target.closest('deferred-media, product-model');
          if (!onMedia && dy > 90 && Math.abs(dx) < 60 && Date.now() - g.time < 700) this.hide();
        }
        if (t.length === 0) this.gesture = null;
      }

      onContentClick(event) {
        if (!this.mql.matches || !this.classList.contains('lb-carousel')) return;
        const img = this.currentImg(event.target);
        const now = Date.now();
        const doubleTap = now - this.lastTap < 300;
        this.lastTap = now;

        if (img && doubleTap) {
          this.lastTap = 0;
          if (this.isZoomed()) {
            this.applyZoom(this.zoom.img, 1, 0, 0, true);
          } else {
            const c = this.centre();
            const s = 2.5;
            const px = event.clientX - c.x;
            const py = event.clientY - c.y;
            this.applyZoom(img, s, px - px * s, py - py * s, true);
          }
          return;
        }

        if (this.isZoomed() || !img) return;
        const box = img.getBoundingClientRect();
        const fit = Math.min(box.width / img.naturalWidth, box.height / img.naturalHeight);
        const w = img.naturalWidth * fit;
        const h = img.naturalHeight * fit;
        const left = box.left + (box.width - w) / 2;
        const top = box.top + (box.height - h) / 2;
        const inside =
          event.clientX >= left && event.clientX <= left + w && event.clientY >= top && event.clientY <= top + h;
        if (!inside) {
          clearTimeout(this.tapTimer);
          this.tapTimer = setTimeout(() => {
            if (this.lastTap === now && !this.isZoomed()) this.hide();
          }, 300);
        }
      }
    }
  );
}
